import { NextPage } from "next";
import { useIntl } from "react-intl";
import Head from "next/head";
import { useEffect, useRef } from "react";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import { SEOWithoutTemplate } from "@/interfaces/seoWithoutTemplate";
import HeroBlock, {
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import { Banner, BannerProps } from "@/components/common/banner/Banner";
import { Section } from "@/components/common/section/Section";
import {
  CompanySearchBlock,
  FidelityBlocksProps,
} from "@/interfaces/loyaltyProgram";
import { FAQProps } from "@/utils/formatCategoryPage";
import styles from "./LoyaltyPrograms.module.scss";
import SearchCompany from "./searchCompany/SearchCompany";
import FidelityBlocks from "./fidelityBlocks/FidelityBlocks";
import { FAQ } from "../vehicle/categoryPage/faq/FAQ";

export type LoyaltyProgramPageContent = {
  heroBlock: HeroBlockProps;
  banner: BannerProps;
  seo: SEO;
  faq?: FAQProps;
  seoWithoutTemplate: SEOWithoutTemplate;
  companySearchBlock: CompanySearchBlock;
  fidelityBlockList: FidelityBlocksProps | null;
};

type LoyaltyProgramPageProps = PageProps &
  LoyaltyProgramPageContent & {
    defaultCanonicalRoute: string;
  };

export const LoyaltyProgram: NextPage<LoyaltyProgramPageProps> = ({
  layout,
  heroBlock,
  banner,
  seo,
  faq,
  seoWithoutTemplate,
  companySearchBlock,
  fidelityBlockList,
  defaultCanonicalRoute,
}: LoyaltyProgramPageProps) => {
  const intl = useIntl();

  const searchRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const hash = window.location.hash;
      if (hash === "#search" && searchRef.current) {
        searchRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, []);

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "http://schema.org/",
              "@type": "Product",
              name: `${seoWithoutTemplate.title}`,
              description: `${seoWithoutTemplate.description}`,
              image: "https://email-assets.rentacar.fr/logo-rac-header.png",
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                price: "29.99",
              },
            }),
          }}
        />
      </Head>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        pageMeta={seo}
        title={seoWithoutTemplate.title}
        description={seoWithoutTemplate.description}
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: `loyaltyProgramBreadcrumb` }),
            url: defaultCanonicalRoute,
          },
        ]}
      >
        <HeroBlock {...heroBlock} hideSearchBlock={heroBlock.hideSearchBlock} />
        <Section
          containerStyle={styles.container}
          sectionStyle={styles.section}
        >
          <Banner {...banner} />
          {fidelityBlockList && <FidelityBlocks {...fidelityBlockList} />}
          <div ref={searchRef}>
            <SearchCompany {...companySearchBlock} />
          </div>
          {faq && <FAQ {...faq} />}
        </Section>
      </Layout>
    </>
  );
};
